import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { moneyFormatFromCents } from '../../../utilities/moneyFormat';
import { commitImportBatch, loadImportPreview } from '../../../redux/actions/import.js';
import Button from '../../components/Button.jsx';
import CircleLoader from '../../components/CircleLoader.jsx';
import ImportRowCategory from './ImportRowCategory.jsx';
import ImportRowNotes from './ImportRowNotes.jsx';
import { blockedCount, combinedOrdersNote, formatRowDate, statusLabel, STATUS_EXPLANATIONS, writableCount } from './importRowDisplay.js';

export default function ImportPreviewTable() {
	const dispatch = useDispatch();
	const batch = useSelector((state) => state.importing.selectedBatch);
	const rows = useSelector((state) => state.importing.previewRows);
	const offset = useSelector((state) => state.importing.previewOffset);
	const status = useSelector((state) => state.importing.previewStatus);
	const filteredTotal = useSelector((state) => state.importing.previewTotal);
	const summary = useSelector((state) => state.importing.summary);
	const loading = useSelector((state) => state.importing.loadingPreview);
	const committing = useSelector((state) => state.importing.committing);
	const committed = useSelector((state) => state.importing.committed);

	if(!batch) {
		return null;
	}

	const pageSize = 100;
	const counts = summary ? summary.counts : {};
	/** Rows a commit would actually write. Everything else is skipped, blocked or already in. */
	const writable = writableCount(counts);
	const blocked = blockedCount(counts);

	/**
	 * Committing is not undoable by deleting the upload, so it asks -- and the question names the
	 * numbers rather than the file, since what matters is how much of it is about to land.
	 */
	const onCommit = () => {
		const question = blocked > 0
			? `Import ${writable} rows now? ${blocked} more are still blocked on a mapping and will be `
				+ 'left staged — map them and import again to finish the file.'
			: `Import ${writable} rows into the budget?`;
		if(!confirm(question)) {
			return;
		}
		dispatch(commitImportBatch(batch.id));
	};

	return (
		<div className='import-preview'>
			<h6>
				Preview{' '}
				{loading && <CircleLoader size='small' />}
			</h6>
			<p className='grey-text'>
				What this file would do to the budget as currently mapped. Nothing has been written yet.
			</p>

			{!!summary &&
			<PreviewSummary
				summary={summary}
				status={status}
				onSelect={(selected) => dispatch(loadImportPreview(batch.id, 0, selected))}
			/>
			}

			{!!status &&
			<p className='import-preview-filter'>
				{filteredTotal > 0
					? `Showing only the ${filteredTotal} ${statusLabel(status).toLowerCase()} rows.`
					: `No rows are ${statusLabel(status).toLowerCase()} any more.`
				}{' '}
				<a href='#' onClick={(event) => { event.preventDefault(); dispatch(loadImportPreview(batch.id, 0, null)); }}>
					Show all
				</a>
			</p>
			}

			{!!committed && <CommitResult committed={committed} />}

			<div className='import-commit'>
				<Button
					onClick={onCommit}
					disabled={committing || loading || writable === 0}
				>
					{committing ? 'Importing…' : `Import ${writable} rows`}
				</Button>
				{committing && <CircleLoader size='small' />}
				{writable === 0 && !committing &&
				<span className='grey-text'>
					{blocked > 0
						? 'Nothing can be imported yet — map the accounts and categories above first.'
						: 'Nothing left to import from this file.'
					}
				</span>
				}
			</div>

			<div className='import-table-scroll'>
				<table className='import-table'>
					<thead>
						<tr>
							<th>Date</th>
							<th>Merchant</th>
							<th>In the file</th>
							<th className='right-align'>Amount</th>
							<th className='right-align'>Bank</th>
							<th className='right-align'>Budget</th>
							<th>Result</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<tr key={row.id} className={`import-row-${row.status}`}>
								<td className='grey-text import-nowrap'>{formatRowDate(row.date)}</td>
								<td>
									{row.merchant}
									<ImportRowNotes notes={row.notes} />
								</td>
								<td className='grey-text'>
									{row.external_category}
									<div className='import-external-key import-wrap'>{row.external_account}</div>
								</td>
								<td className='right-align'>{moneyFormatFromCents(row.amount)}</td>
								<td className='right-align grey-text'>{moneyFormatFromCents(row.bank_amount)}</td>
								<td className='right-align'>
									{row.ledger_amount === null
										? <span className='grey-text'>&mdash;</span>
										: <span title={row.category_name || ''}>
											{moneyFormatFromCents(row.ledger_amount)}
											<div className='import-external-key'>
												{row.status === 'uncategorized' ? 'No category' : row.category_name}
											</div>
										</span>
									}
								</td>
								<td>
									<span className={`import-status import-status-${row.status}`}>
										{statusLabel(row.status)}
									</span>
									{!!row.blocked_on &&
									<div className='import-external-key'>{row.blocked_on}</div>
									}
									<ImportRowCategory row={row} />
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{filteredTotal > pageSize &&
			<div className='import-pager'>
				<Button
					small
					flat
					disabled={offset <= 0}
					onClick={() => dispatch(loadImportPreview(batch.id, Math.max(0, offset - pageSize)))}
				>
					Previous
				</Button>
				<span className='grey-text'>
					{offset + 1}&ndash;{Math.min(offset + pageSize, filteredTotal)} of {filteredTotal}
				</span>
				<Button
					small
					flat
					disabled={offset + pageSize >= filteredTotal}
					onClick={() => dispatch(loadImportPreview(batch.id, offset + pageSize))}
				>
					Next
				</Button>
			</div>
			}
		</div>
	);
}

/**
 * What the last commit wrote. Worth saying explicitly rather than letting the refreshed counts
 * imply it: the rows move from "ready" to "already imported", which looks the same as nothing
 * having happened if you were not watching the number.
 */
function CommitResult({ committed }) {
	const blocked = blockedCount(committed.counts);
	const ordersNote = combinedOrdersNote(committed);

	return (
		<div className='import-commit-result'>
			<span>
				Imported {committed.transactions_written}{' '}
				{committed.transactions_written === 1 ? 'transaction' : 'transactions'}
				{committed.bank_rows_written > committed.transactions_written &&
				<span className='grey-text'>
					{' '}and {committed.bank_rows_written - committed.transactions_written} bank
					{' '}{committed.bank_rows_written - committed.transactions_written === 1 ? 'record' : 'records'}
					{' '}with no budget transaction
				</span>
				}
				{!!ordersNote && <span>{', '}{ordersNote}</span>}
				{committed.transactions_split > 0 &&
				<span>
					{', '}and split {committed.transactions_split} already imported
					{' '}{committed.transactions_split === 1 ? 'transaction' : 'transactions'} into their items
				</span>
				}.
			</span>
			{blocked > 0 &&
			<span className='grey-text'>
				{' '}{blocked} rows are still waiting on a mapping. Map them and import again.
			</span>
			}
		</div>
	);
}

/**
 * One tile per outcome. Clicking a tile narrows the table to those rows; clicking it again, or
 * "Show all", widens it back out.
 */
function PreviewSummary({ summary, status, onSelect }) {
	const entries = Object.keys(summary.counts)
		.map((key) => ({ status: key, count: summary.counts[key] }))
		.filter((entry) => entry.count > 0);

	return (
		<div className='import-summary'>
			{entries.map((entry) => {
				const toggle = () => onSelect(entry.status === status ? null : entry.status);
				/** A div rather than a <button>, which would bring the site's button styling with it. */
				return (
					<div
						role='button'
						tabIndex={0}
						key={entry.status}
						className={`import-summary-tile import-status-${entry.status}`
							+ (entry.status === status ? ' import-summary-tile-active' : '')}
						title={STATUS_EXPLANATIONS[entry.status] || ''}
						aria-pressed={entry.status === status}
						onClick={toggle}
						onKeyDown={(event) => {
							if(event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								toggle();
							}
						}}
					>
						<div className='import-summary-count'>{entry.count}</div>
						<div className='import-summary-label'>{statusLabel(entry.status)}</div>
					</div>
				);
			})}
		</div>
	);
}
